import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { IconUsers, IconHeart, IconEye, IconRefresh, IconCheck } from '@tabler/icons-react';
import { useStore } from 'stores/rootStore';
import { useTheme } from 'hooks/useTheme';
import styles from './Settings.css';

const NUMBER_OF_PLAYERS_OPTIONS = [2, 3, 4, 5, 6];
const STARTING_HEALTH_OPTIONS = [20, 30, 40, 60];

interface Props {
    onSetSettings: () => void;
}

const Settings = observer(({ onSetSettings }: Props) => {
    const { app } = useStore();
    const theme = useTheme();
    const [selectedPlayers, setSelectedPlayers] = useState<number>(app.numberOfPlayers);
    const [selectedHealth, setSelectedHealth] = useState<number>(app.startingHealth);

    const handleSetSettings = () => {
        app.setNumberOfPlayers(selectedPlayers);
        app.setStartingHealth(selectedHealth);
        onSetSettings();
    };

    const handleResetGame = () => {
        app.setupPlayers();
        onSetSettings();
    };

    return (
        <div className={styles.scrollView}>
            <div className={styles.container}>
                {/* Reset Game Button at Top */}
                <button
                    onClick={handleResetGame}
                    className={`${styles.actionButton} ${styles.resetButtonTop}`}
                    style={{ backgroundColor: theme.colors.success }}
                >
                    <IconRefresh size={22} color={theme.colors.buttonText} className={styles.buttonIcon} />
                    <span className={styles.actionButtonText} style={{ color: theme.colors.buttonText }}>
                        Reset Game
                    </span>
                </button>

                {/* Number of Players Card */}
                <div 
                    className={styles.card}
                    style={{ backgroundColor: theme.colors.surfaceElevated }}
                >
                    <div className={styles.cardHeader}>
                        <IconUsers size={22} color={theme.colors.primary} />
                        <span className={styles.cardTitle} style={{ color: theme.colors.text }}>
                            Number of Players
                        </span>
                    </div>
                    <div className={styles.optionsGrid}>
                        {NUMBER_OF_PLAYERS_OPTIONS.map(option => (
                            <button
                                key={option}
                                onClick={() => setSelectedPlayers(option)}
                                className={styles.optionButton}
                                style={{
                                    borderColor: selectedPlayers === option ? theme.colors.primary : theme.colors.borderLight,
                                    backgroundColor: selectedPlayers === option ? theme.colors.primary : theme.colors.surface,
                                }}
                            >
                                <span
                                    className={styles.optionText}
                                    style={{
                                        color: selectedPlayers === option ? theme.colors.buttonText : theme.colors.textSecondary,
                                        fontWeight: selectedPlayers === option ? 700 : 600,
                                    }}
                                >
                                    {option}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Starting Health Card */}
                <div 
                    className={styles.card}
                    style={{ backgroundColor: theme.colors.surfaceElevated }}
                >
                    <div className={styles.cardHeader}>
                        <IconHeart size={22} color={theme.colors.danger} />
                        <span className={styles.cardTitle} style={{ color: theme.colors.text }}>
                            Starting Life
                        </span>
                    </div>
                    <div className={styles.healthOptionsRow}>
                        {STARTING_HEALTH_OPTIONS.map(option => (
                            <button
                                key={option}
                                onClick={() => setSelectedHealth(option)}
                                className={styles.healthOption}
                                style={{
                                    borderColor: selectedHealth === option ? theme.colors.primary : theme.colors.borderLight,
                                    backgroundColor: selectedHealth === option ? theme.colors.primary : theme.colors.surface,
                                }}
                            >
                                <span
                                    className={styles.healthOptionText}
                                    style={{
                                        color: selectedHealth === option ? theme.colors.buttonText : theme.colors.textSecondary,
                                        fontWeight: selectedHealth === option ? 700 : 600,
                                    }}
                                >
                                    {option}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Display Options Card */}
                <div 
                    className={styles.card}
                    style={{ backgroundColor: theme.colors.surfaceElevated }}
                >
                    <div className={styles.cardHeader}>
                        <IconEye size={22} color={theme.colors.success} />
                        <span className={styles.cardTitle} style={{ color: theme.colors.text }}>
                            Display Options
                        </span>
                    </div>
                    <div className={styles.switchRow} style={{ borderTopColor: theme.colors.borderLight }}>
                        <div className={styles.switchLabelContainer}>
                            <span className={styles.switchLabel} style={{ color: theme.colors.text }}>
                                Show Player Names
                            </span>
                            <span className={styles.switchDescription} style={{ color: theme.colors.textMuted }}>
                                Display names on life counters
                            </span>
                        </div>
                        <label className={styles.switch}>
                            <input
                                type="checkbox"
                                checked={app.showPlayerNames}
                                onChange={() => app.togglePlayerNames()}
                            />
                            <span 
                                className={styles.slider}
                                style={{
                                    backgroundColor: app.showPlayerNames 
                                        ? theme.colors.switchTrack.true 
                                        : theme.colors.switchTrack.false
                                }}
                            />
                        </label>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className={styles.buttonsContainer}>
                    <button
                        onClick={handleSetSettings}
                        className={styles.actionButton}
                        style={{ backgroundColor: theme.colors.primary }}
                    >
                        <IconCheck size={22} color={theme.colors.buttonText} className={styles.buttonIcon} />
                        <span className={styles.actionButtonText} style={{ color: theme.colors.buttonText }}>
                            Apply Settings
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
});

export default Settings;
